import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedMediaWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { PromoteMediaDraft } from '../../../music24/promote/draftStore';
import { mediaBlockMessages } from '../../widgets/views/mediaWidget/mediaBlockMessages';
import { MediaBlockPick, pickOfUpload } from '../../widgets/views/mediaWidget/editionView/mediaBlockPick';
import { MediaBlockProblemBox } from '../../widgets/views/mediaWidget/editionView/MediaBlockProblemBox';
import { MediaLookOptions } from '../../widgets/views/mediaWidget/editionView/MediaLookOptions';
import { MediaPlaybackOptions } from '../../widgets/views/mediaWidget/editionView/MediaPlaybackOptions';
import { MediaSourceSection } from '../../widgets/views/mediaWidget/editionView/MediaSourceSection';
import { useMediaBlockDraft } from '../../widgets/views/mediaWidget/editionView/useMediaBlockDraft';
import { useMediaBlockPicker } from '../../widgets/views/mediaWidget/editionView/useMediaBlockPicker';
import { useMediaBlockSong } from '../../widgets/views/mediaWidget/editionView/useMediaBlockSong';
import { BlockAutosave } from './useBlockAutosave';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import mediaStyles from '../../widgets/views/mediaWidget/editionView/MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaBlockFormProps {
  widget: SerializedMediaWidget;
  accountId: number;
  smartLinkId: number;
  autosave: BlockAutosave;
  onDelete: () => void;
}

/**
 * The Media block in the row (design d5): the fields, copy and states of the approved Media design
 * stay; the three panels are one form and Save goes away. Every change saves to the draft.
 */
const MediaBlockFormComponent: React.FC<MediaBlockFormProps> = ({
  widget,
  accountId,
  smartLinkId,
  autosave,
  onDelete,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const draft = useMediaBlockDraft(widget);
  const { choose, data, source } = draft;
  const [isChoosing, setChoosing] = useState(draft.pick === null);
  const song = useMediaBlockSong({ accountId, smartLinkId, source: widget.data.source });
  const { save, markInvalid } = autosave;
  const firstData = useRef(data);

  // Every change after the first render saves; a wrong option saves nothing.
  useEffect(() => {
    if (data === firstData.current) {
      return;
    }
    if (data === null) {
      markInvalid();
      return;
    }
    save(data);
  }, [data, markInvalid, save]);

  const pick = useCallback(
    (next: MediaBlockPick) => {
      choose(next);
      setChoosing(false);
    },
    [choose]
  );
  const onUploaded = useCallback((media: PromoteMediaDraft) => pick(pickOfUpload(intl, media)), [intl, pick]);
  const picker = useMediaBlockPicker({
    accountId,
    song: song.song,
    items: song.items,
    isSongLoading: song.isLoading,
    onUploaded,
  });
  const openChooser = useCallback(() => setChoosing(true), []);
  const openStudio = useCallback(
    () => router.push(SMLRoutes.promoteStudio, song.song === undefined ? undefined : { catalogItemUuid: song.song }),
    [router, song.song]
  );
  // The warning is about the saved file. A new pick replaces it.
  const showsSavedProblem = source === widget.data.source;

  return (
    <div className={mediaStyles.rail} data-testid={`form-${SmartLinkWidgetType.media}`}>
      {showsSavedProblem && <MediaBlockProblemBox data={widget.data} onPickAnother={openChooser} onDelete={onDelete} />}
      <MediaSourceSection
        items={song.items}
        song={song.song}
        onSong={song.changeSong}
        picker={picker}
        pick={draft.pick}
        source={source}
        isChoosing={isChoosing}
        onChoose={openChooser}
        onPick={pick}
        onOpenStudio={openStudio}
      />
      {draft.kind === 'video' && <MediaPlaybackOptions options={draft.options} setOption={draft.setOption} />}
      <MediaLookOptions kind={draft.kind} options={draft.options} setOption={draft.setOption} />
      {data === null && source !== null && (
        <p className={mediaStyles.invalidLine} role="alert">
          {intl.formatMessage(mediaBlockMessages.invalid)}
        </p>
      )}
    </div>
  );
};

export const MediaBlockForm = TypedMemo(MediaBlockFormComponent);
